import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, GitCommitVertical, History, Lock, Pencil, Scale, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTime, fmtUSD, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const btn = "text-[11px] font-semibold flex items-center gap-1 px-3 py-1.5 rounded-full border bg-white transition-colors disabled:opacity-40";
export const fmtLimit = (m, v, meta) => (v == null ? "n/a" : meta[m].fmt === "pct" ? `${(v * 100).toFixed(1)}%` : `${v.toFixed(2)}x`);

function Propose({ cls, metric, d, onDone }) {
  const m = d.metrics[metric];
  const cur = d.limits[cls.key][metric];
  const [val, setVal] = useState(m.fmt === "pct" ? (cur * 100).toFixed(1) : cur.toFixed(2));
  const [reason, setReason] = useState("");
  const send = async () => {
    const v = Number(val) / (m.fmt === "pct" ? 100 : 1);
    try { const { data } = await api.post("/policy/changes", { property_class: cls.key, metric, value: v, reason }); toast.success(`${data.id} submitted for second approval`); onDone(true); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Could not propose"); }
  };
  return (
    <div className="panel p-4 space-y-3 border-sky-200 rise" data-testid="policy-propose-form">
      <div className="text-sm font-semibold text-slate-900">Propose change · {cls.label} · {m.label}</div>
      <div className="flex flex-wrap items-end gap-3 text-xs">
        <div><div className="text-slate-500">Current</div><div className="num text-lg text-slate-900">{fmtLimit(metric, cur, d.metrics)}</div></div>
        <label><div className="text-slate-500">New limit ({m.fmt === "pct" ? "%" : "x"}) · allowed {fmtLimit(metric, m.min, d.metrics)}–{fmtLimit(metric, m.max, d.metrics)}</div>
          <input data-testid="policy-propose-value" value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal" className="num w-28 border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:border-sky-500" /></label>
        <label className="flex-1 min-w-[260px]"><div className="text-slate-500">Business reason (required)</div>
          <input data-testid="policy-propose-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Q3 portfolio review: office valuations down 8% YoY" className="w-full border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:border-sky-500" /></label>
        <button data-testid="policy-propose-submit" disabled={reason.trim().length < 10 || !val} onClick={send} className="btn-primary disabled:opacity-40">Submit for approval</button>
        <button data-testid="policy-propose-cancel" onClick={() => onDone(false)} className="btn-ghost">Cancel</button>
      </div>
      <div className="text-[11px] text-slate-500">Maker-checker: a different person with policy rights must approve before the limit takes effect.</div>
    </div>
  );
}

function Matrix({ d, edit, setEdit }) {
  const pending = new Set(d.pending.map((c) => `${c.property_class}:${c.metric}`));
  return (
    <div className="panel overflow-x-auto thin-scroll" data-testid="policy-matrix">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] text-slate-500 border-b border-slate-200">
          <th className="px-5 py-3 font-semibold">Property type</th>{Object.entries(d.metrics).map(([k, m]) => <th key={k} className="px-4 py-3 font-semibold whitespace-nowrap">{m.label}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {d.classes.map((c) => (
            <tr key={c.key} data-testid={`policy-row-${c.key}`}>
              <td className="px-5 py-3 font-semibold text-slate-900">{c.label}</td>
              {Object.keys(d.metrics).map((m) => {
                const v = d.limits[c.key][m];
                const p = pending.has(`${c.key}:${m}`);
                const on = edit?.cls.key === c.key && edit?.metric === m;
                return (
                  <td key={m} className="px-4 py-2">
                    {v == null ? <span className="text-slate-300 text-xs">n/a</span> : (
                      <button data-testid={`policy-cell-${c.key}-${m}`} disabled={!d.can_edit || p} onClick={() => setEdit({ cls: c, metric: m })}
                        className={`num group flex items-center gap-1.5 px-2.5 py-1 rounded-lg ring-1 transition-colors ${on ? "ring-sky-400 bg-sky-50" : "ring-transparent hover:ring-sky-200"} disabled:cursor-default`}>
                        {fmtLimit(m, v, d.metrics)}
                        {p ? <span className="chip border-amber-200 bg-amber-50 text-amber-700 text-[9px]">pending</span> : d.can_edit && <Pencil className="w-3 h-3 text-slate-300 group-hover:text-sky-600" />}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PolicyChangeRow({ c, me, reload, canEdit }) {
  const [note, setNote] = useState("");
  const self = c.proposed_by === me;
  const act = async (kind) => {
    try {
      const { data } = await api.post(`/policy/changes/${c.id}/${kind}`, { note });
      toast.success(kind === "approve" ? `Approved: now ${data.version}. ${data.impact.length} open loan(s) changed recommendation` : `${c.id} ${self ? "withdrawn" : "rejected"}`);
    } catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Action failed"); }
    reload(); window.dispatchEvent(new Event("omnifi:approvals"));
  };
  return (
    <div className="panel p-4 flex flex-wrap gap-4 items-start" data-testid={`policy-change-${c.id}`}>
      <div className="w-56"><div className="text-[10px] num text-slate-400">{c.id} · from {c.base_version}</div><div className="text-sm font-semibold text-slate-900">{c.class_label} · {c.metric_label}</div>
        <div className="num text-sm mt-0.5"><span className="text-slate-500 line-through">{c.old_fmt}</span> → <span className="text-sky-700 font-semibold">{c.new_fmt}</span></div></div>
      <div className="flex-1 min-w-[220px] text-xs text-slate-700">"{c.reason}"<div className="text-[10px] text-slate-500 mt-1">Proposed by {c.proposed_by_label} · {fmtTime(c.created_at)}</div></div>
      {canEdit && (
        <div className="flex flex-col items-end gap-1.5">
          <input data-testid={`policy-change-note-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder={self ? "Reason to withdraw" : "Approval / rejection note"} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 w-56" />
          <div className="flex gap-1.5">
            <button data-testid={`policy-change-approve-${c.id}`} disabled={self} onClick={() => act("approve")} className={`${btn} border-emerald-500/50 text-emerald-700`}><CheckCircle2 className="w-3 h-3" />Approve</button>
            <button data-testid={`policy-change-reject-${c.id}`} disabled={note.trim().length < 5} onClick={() => act("reject")} className={`${btn} border-red-500/50 text-red-700`}><XCircle className="w-3 h-3" />{self ? "Withdraw" : "Reject"}</button>
          </div>
          {self && <span className="text-[10px] text-red-600 flex items-center gap-1"><Lock className="w-3 h-3" />You proposed this; a second approver is required</span>}
        </div>
      )}
    </div>
  );
}

function Versions({ d }) {
  return (
    <div className="panel" data-testid="policy-versions">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2"><History className="w-4 h-4 text-sky-600" /><span className="text-sm font-semibold text-slate-900">Version history</span></div>
      <div className="divide-y divide-slate-100">
        {d.versions.map((v) => (
          <div key={v.version} className="px-5 py-3 flex gap-3" data-testid={`policy-version-${v.version}`}>
            <GitCommitVertical className={`w-4 h-4 mt-0.5 ${v.version === d.active ? "text-sky-600" : "text-slate-300"}`} />
            <div className="flex-1 text-xs">
              <div className="flex flex-wrap items-center gap-2"><span className="num font-semibold text-slate-900">{v.version}</span>{v.version === d.active && <span className="chip border-sky-200 bg-sky-50 text-sky-700 text-[9px]">active</span>}<span className="text-slate-500">{fmtTime(v.effective_at)} · approved by {v.approved_by}{v.proposed_by !== v.approved_by && ` · proposed by ${v.proposed_by}`}</span></div>
              {v.changes.length ? v.changes.map((c) => <div key={c.id} className="mt-1 text-slate-700">{c.class_label} {c.metric_label}: <span className="num line-through text-slate-400">{c.old_fmt}</span> → <span className="num text-sky-700 font-semibold">{c.new_fmt}</span> <span className="text-slate-500">· {c.reason}</span></div>) : <div className="mt-1 text-slate-500">{v.note}</div>}
              {v.impact?.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1.5">{v.impact.map((i) => <Link key={i.task_id} to={`/task/${i.task_id}?tab=recommendation`} className="chip border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-400">{i.title}: {fmtUSD(i.before_amount, true)} → {fmtUSD(i.after_amount, true)}{i.before !== i.after && ` (${i.after})`}</Link>)}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Policy() {
  const { personaId, personaLabel } = useApp();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/policy/limits").then((r) => setD(r.data)), []);
  useEffect(() => { load(); setEdit(null); }, [load, personaId]);
  if (!d) return <div className="flex-1 p-8 text-sm text-slate-500">Loading policy…</div>;
  return (
    <div className="flex-1 min-w-0 overflow-y-auto thin-scroll grid-bg" data-testid="policy-page">
      <div className="px-6 py-5 border-b border-slate-200/80 bg-white flex flex-wrap items-end justify-between gap-3">
        <div><div className="eyebrow">Credit policy · maker-checker</div><h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3 mt-1"><span className="icon-chip"><Scale className="w-5 h-5" /></span>Lending limits</h1></div>
        <div className="text-right"><div className="chip border-sky-200 bg-sky-50 text-sky-700 num" data-testid="policy-active-version">{d.active}</div><div className="text-[11px] text-slate-500 mt-1">{d.label}</div></div>
      </div>
      <div className="p-6 space-y-5 max-w-[1400px]">
        <div className="text-[11px] text-slate-500">Acting as {personaLabel}. {d.can_edit ? "Click a limit to propose a change." : "Read-only: proposing and approving changes requires the credit-policy permission (Senior Credit Officer or Operations Manager)."} Open loans are re-checked on approval; decided loans keep the version they were decided under.</div>
        <Matrix d={d} edit={edit} setEdit={setEdit} />
        {edit && <Propose key={`${edit.cls.key}-${edit.metric}`} cls={edit.cls} metric={edit.metric} d={d} onDone={(ok) => { setEdit(null); if (ok) { load(); window.dispatchEvent(new Event("omnifi:approvals")); } }} />}
        <div className="space-y-2" data-testid="policy-pending">
          <div className="eyebrow">Awaiting second approval ({d.pending.length})</div>
          {d.pending.length ? d.pending.map((c) => <PolicyChangeRow key={c.id} c={c} me={d.me} reload={load} canEdit={d.can_edit} />) : <div className="text-xs text-slate-500">No pending changes.</div>}
        </div>
        <Versions d={d} />
        {d.history.length > 0 && (
          <div className="panel p-4 text-xs space-y-1" data-testid="policy-decided">
            <div className="eyebrow mb-1">Decided requests</div>
            {d.history.map((c) => <div key={c.id} className="text-slate-600"><span className="num text-slate-400">{c.id}</span> {c.class_label} {c.metric_label} {c.old_fmt} → {c.new_fmt} · <span className={c.status === "Approved" ? "text-emerald-700" : "text-red-700"}>{c.status}</span> by {c.decided_by_label}{c.decision_note && ` - "${c.decision_note}"`}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
